import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { walletAPI, withdrawalAPI, adminAPI } from '../api/client';
import './DashboardPage.css';

export const DashboardPage = () => {
  const { user, logout } = useAuth();
  const [activeMenu, setActiveMenu] = useState('overview');
  const [walletData, setWalletData] = useState(null);

  // Add Money states
  const [addMoneyAmount, setAddMoneyAmount] = useState('');
  const [addMoneyError, setAddMoneyError] = useState('');
  const [addMoneyLoading, setAddMoneyLoading] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [isPolling, setIsPolling] = useState(false);

  // Withdraw states
  const [paymentMethod, setPaymentMethod] = useState('bank');
  const [withdrawFormData, setWithdrawFormData] = useState({
    amount: '',
    accountNumber: '',
    ifsc: '',
    accountHolderName: '',
    bankName: '',
    usdtAddress: '',
    usdtNetwork: 'ERC20',
  });
  const [withdrawError, setWithdrawError] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  // Admin - Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('');

  // Admin - API Config state
  const [apiConfig, setApiConfig] = useState({ merchant_id: '', payin_api_key: '', payout_api_key: '' });
  const [showPasswords, setShowPasswords] = useState({ payin: false, payout: false });
  const [apiConfigSaving, setApiConfigSaving] = useState(false);
  const [apiConfigSuccess, setApiConfigSuccess] = useState('');

  // Admin - Column search filters
  const [columnFilters, setColumnFilters] = useState({
    username: '',
    email: '',
    accountStatus: '',
    depositCommission: '',
    withdrawCommission: '',
    referralCommission: '',
  });

  // Admin - Inline editing
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  // Admin - User history modal
  const [selectedUserHistory, setSelectedUserHistory] = useState(null);
  const [userTransactions, setUserTransactions] = useState([]);
  const [transactionSearch, setTransactionSearch] = useState('');

  // Admin - Default commissions
  const [defaultCommissions, setDefaultCommissions] = useState({
    referral: 5,
    deposit: 10,
    withdraw: 10,
  });
  const [editingDefaults, setEditingDefaults] = useState(false);
  const [defaultValues, setDefaultValues] = useState({ referral: 5, deposit: 10, withdraw: 10 });

  // Profile states
  const [profileForm, setProfileForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Admin - Signup pause state
  const [signupPaused, setSignupPaused] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  // Admin - User account management state
  const [accountActionUser, setAccountActionUser] = useState(null);
  const [accountActionMenu, setAccountActionMenu] = useState(false);
  const [accountActionLoading, setAccountActionLoading] = useState(false);

  // Admin - Limits & Stats
  const [systemLimits, setSystemLimits] = useState({ min_add_amount: 200, min_withdraw_amount: 100 });
  const [editingLimits, setEditingLimits] = useState(false);
  const [limitValues, setLimitValues] = useState({ min_add_amount: 200, min_withdraw_amount: 100 });

  // User transactions (for transaction history tab)
  const [myTransactions, setMyTransactions] = useState([]);

  useEffect(() => {
    fetchWalletData();
    if (user?.isAdmin) {
      fetchSignupStatus();
    }
  }, [user?.isAdmin]);

  const fetchWalletData = async () => {
    try {
      const response = await walletAPI.getBalance();
      setWalletData(response.data.data);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    }
  };

  const fetchStatsAndLimits = async () => {
    try {
      const limitsRes = await adminAPI.getLimits();
      const limits = limitsRes.data.data || { min_add_amount: 200, min_withdraw_amount: 100 };
      setSystemLimits(limits);
      setLimitValues(limits);
    } catch (err) {
      console.error('Error fetching limits:', err);
    }
  };

  const fetchMyTransactions = async () => {
    try {
      const response = await walletAPI.getTransactions();
      setMyTransactions(response.data.data || []);
    } catch (err) {
      console.error('Error fetching my transactions:', err);
    }
  };

  const fetchBondpayBalance = async () => {
    try {
      const response = await adminAPI.fetchBalance();
      const newBalance = response.data.data.balance;
      setWalletData(prev => ({ ...prev, walletBalance: newBalance }));
    } catch (err) {
      console.error('Error fetching BondPay balance:', err);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await adminAPI.getUsers({ search: userSearch, status: userStatusFilter });
      setUsers(response.data.data || []);
      
      // Also load latest default commissions
      try {
        const defaultCommsResponse = await adminAPI.getDefaultCommissions();
        if (defaultCommsResponse.data?.data) {
          setDefaultCommissions(defaultCommsResponse.data.data);
        }
      } catch (err) {
        console.error('Error loading default commissions:', err);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchApiConfig = async () => {
    try {
      const response = await adminAPI.getPaymentConfig();
      setApiConfig(response.data.data || { merchant_id: '', payin_api_key: '', payout_api_key: '' });
    } catch (error) {
      console.error('Error fetching API config:', error);
    }
  };

  // Load admin data when switching to admin menus
  useEffect(() => {
    if (activeMenu === 'manage-users' && users.length === 0) {
      fetchUsers();
      fetchStatsAndLimits();
    }
    if (activeMenu === 'api-config') {
      fetchApiConfig();
    }
    if (activeMenu === 'overview' && user?.isAdmin) {
      fetchWalletData();
      fetchBondpayBalance();
    }
    if (activeMenu === 'transactions') {
      fetchMyTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenu]);

  // Refetch users when search or status filter changes
  useEffect(() => {
    if (activeMenu === 'manage-users') {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch, userStatusFilter]);

  // Payment status polling
  useEffect(() => {
    if (!isPolling || !paymentData) return;

    const pollInterval = setInterval(async () => {
      try {
        const statusResponse = await walletAPI.getPaymentStatus(paymentData.orderNo);
        const status = statusResponse.data.data.status;

        if (status === 'success') {
          setPaymentStatus('success');
          setIsPolling(false);
          clearInterval(pollInterval);
          fetchWalletData();
        } else if (status === 'failed') {
          setPaymentStatus('failed');
          setIsPolling(false);
          clearInterval(pollInterval);
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [isPolling, paymentData]);

  // Close dropdown menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const accountMenuElement = document.getElementById('account-action-dropdown');
      const gearButtons = document.querySelectorAll('[data-user-action-btn]');
      
      // Check if click is outside both the dropdown and ALL gear buttons
      let isClickedInsideDropdown = accountMenuElement?.contains(e.target);
      let isClickedOnGearButton = false;
      
      gearButtons.forEach(btn => {
        if (btn.contains(e.target)) {
          isClickedOnGearButton = true;
        }
      });
      
      // Close dropdown if clicked outside both
      if (!isClickedInsideDropdown && !isClickedOnGearButton) {
        setAccountActionMenu(false);
      }
    };

    if (accountActionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [accountActionMenu]);

  const handleCopyReferralCode = () => {
    if (walletData?.referralCode) {
      navigator.clipboard.writeText(walletData.referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleAddMoney = async (e) => {
    e.preventDefault();
    setAddMoneyError('');
    
    const parsedAmount = parseFloat(addMoneyAmount);
    
    if (parsedAmount < 200) {
      setAddMoneyError('Minimum amount required is ₹200');
      return;
    }
    
    setAddMoneyLoading(true);

    try {
      const response = await walletAPI.addMoney(parsedAmount);
      setPaymentData(response.data.data);
      setPaymentStatus('pending');
      setIsPolling(true);
    } catch (err) {
      setAddMoneyError(err.response?.data?.message || 'Error adding money');
      setAddMoneyLoading(false);
    }
  };

  const validateUSDTAddress = (address, network) => {
    if (network === 'ERC20') {
      return /^0x[a-fA-F0-9]{40}$/.test(address);
    } else if (network === 'TRC20') {
      return /^T[a-zA-Z0-9]{33}$/.test(address);
    }
    return false;
  };

  const handleWithdrawChange = (e) => {
    const { name, value } = e.target;
    setWithdrawFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setWithdrawError('');
    setWithdrawLoading(true);

    try {
      const requestData = {
        amount: parseFloat(withdrawFormData.amount),
        paymentMethod: paymentMethod,
      };

      if (paymentMethod === 'bank') {
        if (!withdrawFormData.accountNumber || !withdrawFormData.ifsc || !withdrawFormData.accountHolderName || !withdrawFormData.bankName) {
          setWithdrawError('All bank details are required');
          setWithdrawLoading(false);
          return;
        }
        requestData.accountNumber = withdrawFormData.accountNumber;
        requestData.ifsc = withdrawFormData.ifsc;
        requestData.accountHolderName = withdrawFormData.accountHolderName;
        requestData.bankName = withdrawFormData.bankName;
      } else if (paymentMethod === 'usdt') {
        if (!withdrawFormData.usdtAddress || !withdrawFormData.usdtNetwork) {
          setWithdrawError('USDT address and network are required');
          setWithdrawLoading(false);
          return;
        }
        if (!validateUSDTAddress(withdrawFormData.usdtAddress, withdrawFormData.usdtNetwork)) {
          setWithdrawError(`Invalid ${withdrawFormData.usdtNetwork} address format`);
          setWithdrawLoading(false);
          return;
        }
        requestData.usdtAddress = withdrawFormData.usdtAddress;
        requestData.usdtNetwork = withdrawFormData.usdtNetwork;
      }

      await withdrawalAPI.request(requestData);
      setWithdrawSuccess(true);
      setTimeout(() => {
        setWithdrawSuccess(false);
        setWithdrawFormData({ amount: '', accountNumber: '', ifsc: '', accountHolderName: '', bankName: '', usdtAddress: '', usdtNetwork: 'ERC20' });
        setActiveMenu('overview');
      }, 2000);
    } catch (err) {
      setWithdrawError(err.response?.data?.message || 'Error processing withdrawal');
    } finally {
      setWithdrawLoading(false);
    }
  };

  // Admin handlers
  const handleFreezeUser = async (userId) => {
    if (!window.confirm('Are you sure you want to freeze this user?')) return;
    try {
      await adminAPI.freezeUser(userId);
      fetchUsers();
    } catch (error) {
      console.error('Error freezing user:', error);
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await adminAPI.activateUser(userId);
      fetchUsers();
    } catch (error) {
      console.error('Error activating user:', error);
    }
  };

  const handleSaveApiConfig = async (e) => {
    e.preventDefault();
    setApiConfigSaving(true);
    try {
      await adminAPI.updatePaymentConfig(apiConfig);
      setApiConfigSuccess('✅ Configuration saved successfully!');
      setTimeout(() => setApiConfigSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving API config:', error);
    } finally {
      setApiConfigSaving(false);
    }
  };

  const handleApiConfigChange = (e) => {
    const { name, value } = e.target;
    setApiConfig(prev => ({ ...prev, [name]: value }));
  };

  // Database export/import handlers
  const exportDatabase = async () => {
    try {
      const response = await adminAPI.exportDatabase();
      if (response.data.success) {
        const dataStr = JSON.stringify(response.data.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `paysolutions_database_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        alert('✅ Database exported successfully!');
      }
    } catch (error) {
      console.error('Error exporting database:', error);
      alert('Error exporting database');
    }
  };

  const importDatabase = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const fileContent = await file.text();
      const data = JSON.parse(fileContent);
      
      if (!data.users || !Array.isArray(data.users)) {
        alert('Invalid file format. Expected users array.');
        return;
      }

      const confirmed = window.confirm(
        `⚠️ This will overwrite all data with ${data.users.length} users.\n\nContinue?`
      );
      
      if (!confirmed) {
        e.target.value = ''; // Reset file input
        return;
      }

      const response = await adminAPI.importDatabase(data);
      if (response.data.success) {
        alert('✅ ' + response.data.message);
        // Reload users list
        fetchUsers();
      }
    } catch (error) {
      console.error('Error importing database:', error);
      alert('Error importing database: ' + (error.response?.data?.message || error.message));
    } finally {
      e.target.value = ''; // Reset file input
    }
  };

  // Admin - Column filter handlers
  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({ ...prev, [column]: value }));
    
    // Also update the backend filter state for account status
    if (column === 'accountStatus') {
      setUserStatusFilter(value);
    }
  };

  const getFilteredUsers = () => {
    return users.filter(u => {
      // For account status, use exact match (case-insensitive)
      const statusMatch = !columnFilters.accountStatus || 
        u.account_status?.toLowerCase() === columnFilters.accountStatus.toLowerCase();
      
      return (
        u.username?.toLowerCase().includes(columnFilters.username.toLowerCase()) &&
        u.email?.toLowerCase().includes(columnFilters.email.toLowerCase()) &&
        statusMatch &&
        (!columnFilters.depositCommission || (u.depositCommission || 0)?.toString().includes(columnFilters.depositCommission)) &&
        (!columnFilters.withdrawCommission || (u.withdrawCommission || 0)?.toString().includes(columnFilters.withdrawCommission)) &&
        (!columnFilters.referralCommission || (u.referralCommission || 0)?.toString().includes(columnFilters.referralCommission))
      );
    });
  };

  // Admin - Inline editing handlers
  const startEditing = (userId, field, value) => {
    setEditingUserId(userId);
    setEditingField(field);
    setEditingValue(value.toString());
  };

  const saveEdit = async (userId) => {
    if (!editingField) return;
    try {
      const updatePayload = {};
      
      if (editingField === 'balance') {
        updatePayload.walletBalance = parseFloat(editingValue);
      } else if (editingField === 'depositCommission') {
        updatePayload.depositCommission = parseFloat(editingValue);
      } else if (editingField === 'withdrawCommission') {
        updatePayload.withdrawCommission = parseFloat(editingValue);
      } else if (editingField === 'referralCommission') {
        updatePayload.referralCommission = parseFloat(editingValue);
      }

      // Call API to update user data
      await adminAPI.updateUserData(userId, updatePayload);
      
      setEditingUserId(null);
      setEditingField(null);
      alert('✅ Updated successfully!');
      
      // Refresh users data from backend to ensure persistence
      await fetchUsers();
    } catch (error) {
      console.error('Error saving edit:', error);
      alert('Error updating. Please try again.');
    }
  };

  const cancelEdit = () => {
    setEditingUserId(null);
    setEditingField(null);
    setEditingValue('');
  };

  // Admin - User history modal handlers
  const openUserHistory = async (user) => {
    setSelectedUserHistory(user);
    try {
      const response = await adminAPI.getUserTransactions(user.id);
      setUserTransactions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching user transactions:', error);
      setUserTransactions([]);
    }
  };

  const closeUserHistory = () => {
    setSelectedUserHistory(null);
    setUserTransactions([]);
    setTransactionSearch('');
  };

  const getFilteredTransactions = () => {
    return userTransactions.filter(t =>
      t.orderNo?.toLowerCase().includes(transactionSearch.toLowerCase()) ||
      t.amount?.toString().includes(transactionSearch) ||
      t.type?.toLowerCase().includes(transactionSearch.toLowerCase()) ||
      t.status?.toLowerCase().includes(transactionSearch.toLowerCase())
    );
  };

  // Admin - Default commissions handlers
  const handleDefaultCommissionChange = (field, value) => {
    setDefaultValues(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const saveDefaultCommissions = async () => {
    try {
      // Call API to save default commissions
      await adminAPI.setDefaultCommissions({
        referral: defaultValues.referral,
        deposit: defaultValues.deposit,
        withdraw: defaultValues.withdraw,
      });
      
      // Update local state after successful API call
      setDefaultCommissions({ ...defaultValues });
      setEditingDefaults(false);
      alert('Default commissions updated successfully!');
    } catch (error) {
      console.error('Error saving default commissions:', error);
      alert('Error updating commissions. Please try again.');
    }
  };

  const fetchSignupStatus = async () => {
    try {
      const response = await adminAPI.getSignupStatus();
      console.log('✅ Signup status response:', response.data);
      setSignupPaused(response.data.data?.signupPaused || false);
    } catch (error) {
      console.error('❌ Error fetching signup status:', error);
    }
  };

  const handleToggleSignupPause = async () => {
    setToggleLoading(true);
    try {
      const newStatus = !signupPaused;
      console.log('🔄 Toggling signup pause to:', newStatus);
      const response = await adminAPI.toggleSignupPause(newStatus);
      console.log('✅ Toggle response:', response.data);
      setSignupPaused(newStatus);
      alert(`✅ Signups ${newStatus ? 'paused' : 'resumed'} successfully!`);
    } catch (error) {
      console.error('❌ Error toggling signup pause:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setToggleLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileMessage('');
    setProfileLoading(true);

    try {
      if (!profileForm.oldPassword || !profileForm.newPassword || !profileForm.confirmPassword) {
        setProfileError('All fields are required');
        setProfileLoading(false);
        return;
      }

      if (profileForm.newPassword !== profileForm.confirmPassword) {
        setProfileError('New passwords do not match');
        setProfileLoading(false);
        return;
      }

      if (profileForm.newPassword.length < 6) {
        setProfileError('New password must be at least 6 characters');
        setProfileLoading(false);
        return;
      }

      const { authAPI } = await import('../api/client.js');
      await authAPI.changePassword({
        oldPassword: profileForm.oldPassword,
        newPassword: profileForm.newPassword,
        confirmPassword: profileForm.confirmPassword,
      });

      setProfileMessage('✅ Password changed successfully!');
      setProfileForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setProfileMessage(''), 3000);
    } catch (error) {
      setProfileError(error.response?.data?.message || 'Error changing password');
    } finally {
      setProfileLoading(false);
    }
  };

  // Handle user account actions (freeze, terminate, delete, etc)
  const handleUserAction = async (action) => {
    try {
      setAccountActionLoading(true);
      const userId = accountActionUser.id;
      
      if (action === 'freeze') {
        console.log('❄️ Freezing user:', userId);
        await adminAPI.freezeUser(userId);
        alert(`✅ User ${accountActionUser.username} frozen successfully`);
      } else if (action === 'unfreeze') {
        console.log('☀️ Unfreezing user:', userId);
        await adminAPI.unfreezeUser(userId);
        alert(`✅ User ${accountActionUser.username} unfrozen successfully`);
      } else if (action === 'terminate') {
        console.log('⛔ Terminating user:', userId);
        await adminAPI.terminateUser(userId);
        alert(`✅ User ${accountActionUser.username} terminated successfully`);
      } else if (action === 'unterminate') {
        console.log('🔓 Unterminating user:', userId);
        await adminAPI.unterminateUser(userId);
        alert(`✅ User ${accountActionUser.username} unterminated successfully`);
      } else if (action === 'pause-withdrawal') {
        console.log('⏸️ Pausing withdrawals for user:', userId);
        await adminAPI.pauseWithdrawal(userId);
        alert(`✅ Withdrawals paused for user ${accountActionUser.username}`);
      } else if (action === 'resume-withdrawal') {
        console.log('▶️ Resuming withdrawals for user:', userId);
        await adminAPI.resumeWithdrawal(userId);
        alert(`✅ Withdrawals resumed for user ${accountActionUser.username}`);
      } else if (action === 'delete') {
        // eslint-disable-next-line no-restricted-globals
        if (confirm(`Are you sure you want to permanently delete user ${accountActionUser.username}? This cannot be undone.`)) {
          console.log('🗑️ Deleting user:', userId);
          await adminAPI.deleteUser(userId);
          alert(`✅ User ${accountActionUser.username} deleted successfully`);
        } else {
          setAccountActionLoading(false);
          return;
        }
      }

      // Refresh users list
      await fetchUsers();
      setAccountActionMenu(false);
      setAccountActionUser(null);
    } catch (error) {
      alert(`❌ Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setAccountActionLoading(false);
    }
  };

  const menuItems = [
    { id: 'overview', icon: '📊', label: 'Overview' },
    { id: 'profile', icon: '👤', label: 'Profile' },
    { id: 'add-money', icon: '💳', label: 'Add Money' },
    { id: 'withdraw', icon: '🏦', label: 'Withdraw' },
    { id: 'transactions', icon: '📋', label: 'Transactions' },
    { id: 'withdrawals', icon: '📤', label: 'Withdrawals' },
    { id: 'referrals', icon: '🎯', label: 'Referrals' },
    ...(user?.isAdmin ? [
      { id: 'manage-users', icon: '👥', label: 'Manage Users' },
      { id: 'api-config', icon: '⚙️', label: 'API Config' },
    ] : []),
  ];

  const renderContent = () => {
    switch (activeMenu) {
      case 'overview':
        return (
          <div className="text-white">
            <h3 className="text-2xl font-bold mb-4">Welcome, {user?.username}! 👋</h3>
            <p className="text-gray-400 mb-6">Manage your wallet and track your digital assets</p>
            <div className="grid grid-cols-2 gap-4 max-w-xl">
              <div className="p-4">
                <p className="text-gray-400 text-sm mb-2">
                  Wallet Balance
                  {user?.isAdmin && (
                    <button onClick={fetchBondpayBalance} className="ml-2 text-xs bg-yellow-600 hover:bg-yellow-700 px-2 py-0.5 rounded" title="Fetch BondPay Balance">🔄 Fetch</button>
                  )}
                </p>
                <p className="text-2xl font-bold text-yellow-600">₹{parseFloat(walletData?.walletBalance || 0).toFixed(2)}</p>
                {user?.isAdmin && <p className="text-xs text-gray-500 mt-1">BondPay Total Balance</p>}
              </div>
              <div className="p-4">
                <p className="text-gray-400 text-sm mb-2">Referral Earnings</p>
                <p className="text-2xl font-bold text-yellow-600">₹{parseFloat(walletData?.referralEarnings?.total_earned || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
        );

      case 'profile':
        return (
          <div className="text-white max-w-md">
            <h3 className="text-2xl font-bold mb-4">User Profile</h3>
            <p className="text-gray-400 mb-6">Manage your account settings</p>

            {/* User Info Section */}
            <div className="bg-slate-700/30 rounded-lg p-6 mb-6">
              <h4 className="text-lg font-bold mb-4">Account Information</h4>
              
              <div className="mb-4">
                <p className="text-gray-400 text-sm mb-1">Username</p>
                <p className="text-lg font-mono text-yellow-600">{user?.username}</p>
              </div>

              <div className="mb-4">
                <p className="text-gray-400 text-sm mb-1">Password</p>
                <p className="text-lg font-mono text-gray-300">
                  {showPassword ? '••••••••••' : '••••••••••'}
                </p>
              </div>
            </div>

            {/* Change Password Section */}
            <div className="bg-slate-700/30 rounded-lg p-6">
              <h4 className="text-lg font-bold mb-4">Change Password</h4>

              {profileMessage && (
                <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded mb-4">
                  {profileMessage}
                </div>
              )}

              {profileError && (
                <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded mb-4">
                  {profileError}
                </div>
              )}

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Current Password</label>
                  <input
                    type="password"
                    value={profileForm.oldPassword}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, oldPassword: e.target.value }))}
                    placeholder="Enter current password"
                    className="w-full bg-slate-600 border border-slate-500 text-white rounded px-4 py-2 focus:outline-none focus:border-yellow-600"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">New Password</label>
                  <input
                    type="password"
                    value={profileForm.newPassword}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    placeholder="Enter new password (min 6 characters)"
                    className="w-full bg-slate-600 border border-slate-500 text-white rounded px-4 py-2 focus:outline-none focus:border-yellow-600"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={profileForm.confirmPassword}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder="Confirm new password"
                    className="w-full bg-slate-600 border border-slate-500 text-white rounded px-4 py-2 focus:outline-none focus:border-yellow-600"
                  />
                </div>

                <button
                  type="submit"
                  disabled={profileLoading}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-600/50 text-white font-bold py-2 px-4 rounded transition"
                >
                  {profileLoading ? 'Updating...' : 'Change Password'}
                </button>
              </form>
            </div>
          </div>
        );

      case 'add-money':
        if (paymentStatus === 'success' && paymentData) {
          return (
            <div className="text-white max-w-md">
              <div className="p-8 text-center">
                <div className="text-5xl mb-4">✅</div>
                <h2 className="text-2xl font-bold text-green-400 mb-2">Payment Received!</h2>
                <p className="text-gray-300 mb-6">Your wallet has been credited</p>
                <div className="p-4 mb-4 text-left">
                  <p className="text-sm text-gray-400 mb-2">Order ID</p>
                  <p className="text-lg font-bold text-yellow-600 break-all">{paymentData.orderNo}</p>
                  <p className="text-xs text-gray-500 mt-2">Please note down this order ID</p>
                </div>
                <div className="p-4 mb-4 text-left space-y-2">
                  <p className="text-sm"><strong className="text-gray-400">Amount Paid:</strong> <span className="text-yellow-600">₹{paymentData.grossAmount}</span></p>
                  <p className="text-sm"><strong className="text-gray-400">Commission:</strong> <span className="text-yellow-600">₹{paymentData.commission}</span></p>
                  <p className="text-sm"><strong className="text-green-400">Wallet Credited:</strong> <span className="text-yellow-600">₹{paymentData.walletWillReceive}</span></p>
                </div>
                <button onClick={() => { setPaymentData(null); setAddMoneyAmount(''); setActiveMenu('overview'); }} className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded-lg">Back</button>
              </div>
            </div>
          );
        }

        if (paymentStatus === 'failed' && paymentData) {
          return (
            <div className="text-white max-w-md">
              <div className="p-8 text-center">
                <div className="text-5xl mb-4">❌</div>
                <h2 className="text-2xl font-bold text-red-400 mb-2">Payment Failed or Expired</h2>
                <p className="text-gray-300 mb-6">Your payment was not completed. No money was deducted from your wallet.</p>
                <div className="p-4 mb-4 text-left bg-slate-800 rounded-lg border border-slate-700">
                  <p className="text-sm text-gray-400 mb-2">Order ID</p>
                  <p className="text-sm font-mono font-bold text-gray-300 break-all">{paymentData.orderNo}</p>
                </div>
                <div className="text-left text-sm text-gray-400 mb-6 space-y-2">
                  <p><strong>Amount Attempted:</strong> ₹{paymentData.grossAmount}</p>
                  <p><strong>Status:</strong> Failed / Expired</p>
                </div>
                <button
                  onClick={() => { setPaymentData(null); setPaymentStatus('pending'); setAddMoneyError(''); }}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded-lg"
                >
                  Try Again
                </button>
                <button
                  onClick={() => { setPaymentData(null); setPaymentStatus('pending'); setAddMoneyAmount(''); setActiveMenu('overview'); }}
                  className="w-full mt-2 bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-4 rounded-lg"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          );
        }

        if (paymentData) {
          return (
            <div className="text-white max-w-2xl w-full">
              <div className="p-6">
                <h2 className="text-xl font-bold mb-4">Complete Your Payment</h2>
                <div className="bg-green-500/20 border border-green-500 p-3 rounded-lg mb-4">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full"></div>
                    <p className="text-sm">Tracking your payment...</p>
                  </div>
                </div>
                <p className="text-xs text-amber-400 mb-2">⏱️ Please complete your payment before the timer on the page expires</p>
                <div className="mb-4">
                  <iframe
                    src={paymentData.paymentUrl}
                    title="Payment Page"
                    className="w-full rounded-lg border-2 border-slate-600 bg-white"
                    style={{ height: '560px' }}
                    allow="payment"
                  />
                </div>
                <div className="p-4 mb-4">
                  <p className="text-sm text-gray-400 mb-1">Your wallet will receive</p>
                  <p className="text-lg font-bold text-yellow-600">₹{paymentData.walletWillReceive}</p>
                  <p className="text-xs text-gray-400 mt-1">Commission: ₹{paymentData.commission}</p>
                </div>
                <div className="space-y-2 mb-4 text-sm text-gray-400">
                  <p><strong>Amount:</strong> ₹{paymentData.grossAmount}</p>
                  <p><strong>Commission ({defaultCommissions.deposit}%):</strong> ₹{paymentData.commission}</p>
                </div>
                <a href={paymentData.paymentUrl} target="_blank" rel="noopener noreferrer" className="w-full block text-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg mb-2">Open in new tab ↗</a>
                <p className="text-center text-xs text-gray-500">If you have already paid, do not pay again</p>
              </div>
            </div>
          );
        }

        return (
          <div className="text-white max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Add Money to Wallet</h2>
              {addMoneyError && (
                <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded mb-4">{addMoneyError}</div>
              )}
              <form onSubmit={handleAddMoney} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Enter Amount (₹)</label>
                  <input
                    type="number"
                    value={addMoneyAmount}
                    onChange={(e) => setAddMoneyAmount(e.target.value)}
                    required
                    min="200"
                    step="1"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:border-yellow-600"
                    placeholder="200"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum: ₹200</p>
                </div>
                {addMoneyAmount && (
                  <div className="bg-blue-500/20 border border-blue-500 p-3 rounded">
                    <p className="text-sm text-gray-400">Commission ({defaultCommissions.deposit}%): ₹{(parseFloat(addMoneyAmount) * (defaultCommissions.deposit / 100)).toFixed(2)}</p>
                    <p className="text-sm font-bold text-yellow-600 mt-1">You'll Get: ₹{(parseFloat(addMoneyAmount) * (1 - defaultCommissions.deposit / 100)).toFixed(2)}</p>
                  </div>
                )}
                <button type="submit" disabled={addMoneyLoading} className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded-lg transition disabled:opacity-50">
                  {addMoneyLoading ? 'Processing...' : 'Continue to Payment'}
                </button>
              </form>
            </div>
          </div>
        );

      case 'withdraw':
        if (withdrawSuccess) {
          return (
            <div className="text-white max-w-md">
              <div className="bg-green-500/20 border border-green-500 rounded-lg p-8 text-center">
                <div className="text-5xl mb-4">✅</div>
                <h2 className="text-2xl font-bold text-green-400 mb-2">Withdrawal Requested!</h2>
                <p className="text-gray-300">Your withdrawal is being processed</p>
              </div>
            </div>
          );
        }

        return (
          <div className="text-white max-w-2xl">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-6">Withdraw Money</h2>
              {withdrawError && (
                <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded mb-4">{withdrawError}</div>
              )}
              <form onSubmit={handleWithdraw} className="space-y-6">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Amount (₹)</label>
                  <input
                    type="number"
                    name="amount"
                    value={withdrawFormData.amount}
                    onChange={handleWithdrawChange}
                    required
                    min="1"
                    step="1"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:border-yellow-600"
                    placeholder="1000"
                  />
                  {withdrawFormData.amount && (
                    <p className="text-xs text-gray-400 mt-1">You'll receive: ₹{(parseFloat(withdrawFormData.amount) * (1 - defaultCommissions.withdraw / 100)).toFixed(2)} ({defaultCommissions.withdraw}% commission)</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-3">Withdrawal Method</label>
                  <div className="space-y-3">
                    <label className={`flex items-center p-4 border rounded-lg cursor-pointer transition ${paymentMethod === 'bank' ? 'bg-yellow-600/20 border-yellow-600' : 'bg-slate-700 border-slate-600'}`}>
                      <input type="radio" value="bank" checked={paymentMethod === 'bank'} onChange={(e) => setPaymentMethod(e.target.value)} className="w-4 h-4" />
                      <span className="ml-3"><strong>Bank Transfer</strong><p className="text-xs text-gray-400">1-2 hours</p></span>
                    </label>
                    <label className={`flex items-center p-4 border rounded-lg cursor-pointer transition ${paymentMethod === 'usdt' ? 'bg-purple-600/20 border-purple-600' : 'bg-slate-700 border-slate-600'}`}>
                      <input type="radio" value="usdt" checked={paymentMethod === 'usdt'} onChange={(e) => setPaymentMethod(e.target.value)} className="w-4 h-4" />
                      <span className="ml-3"><strong>USDT (Crypto)</strong><p className="text-xs text-gray-400">10-30 minutes</p></span>
                    </label>
                  </div>
                </div>

                {paymentMethod === 'bank' && (
                  <div className="bg-blue-500/20 border border-blue-500 rounded-lg p-4 space-y-3">
                    <h3 className="font-bold text-sm">Bank Account Details</h3>
                    <input type="text" name="accountNumber" value={withdrawFormData.accountNumber} onChange={handleWithdrawChange} required placeholder="Account Number" className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-600" />
                    <input type="text" name="ifsc" value={withdrawFormData.ifsc} onChange={handleWithdrawChange} required placeholder="IFSC Code" className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-600" />
                    <input type="text" name="accountHolderName" value={withdrawFormData.accountHolderName} onChange={handleWithdrawChange} required placeholder="Account Holder Name" className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-600" />
                    <input type="text" name="bankName" value={withdrawFormData.bankName} onChange={handleWithdrawChange} required placeholder="Bank Name" className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-600" />
                  </div>
                )}

                {paymentMethod === 'usdt' && (
                  <div className="bg-purple-500/20 border border-purple-500 rounded-lg p-4 space-y-3">
                    <h3 className="font-bold text-sm">USDT Wallet Details</h3>
                    <select name="usdtNetwork" value={withdrawFormData.usdtNetwork} onChange={handleWithdrawChange} className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-600">
                      <option value="ERC20">ERC20 (Ethereum)</option>
                      <option value="TRC20">TRC20 (Tron)</option>
                    </select>
                    <p className="text-xs text-gray-400 mt-1">
                      {withdrawFormData.usdtNetwork === 'ERC20' 
                        ? 'Ethereum network - Address must be 42 characters starting with 0x'
                        : 'Tron network - Address must be 34 characters starting with T'}
                    </p>
                    <input type="text" name="usdtAddress" value={withdrawFormData.usdtAddress} onChange={handleWithdrawChange} required placeholder={withdrawFormData.usdtNetwork === 'ERC20' ? '0x...' : 'T...'} className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-600 font-mono" />
                    <p className="text-xs text-yellow-400">⚠️ Double-check the address - transactions cannot be reversed</p>
                  </div>
                )}

                <button type="submit" disabled={withdrawLoading} className={`w-full font-bold py-2 px-4 rounded-lg transition disabled:opacity-50 ${paymentMethod === 'bank' ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'} text-white`}>
                  {withdrawLoading ? 'Processing...' : `Request ${paymentMethod === 'usdt' ? 'USDT' : 'Withdrawal'}`}
                </button>
              </form>
            </div>
          </div>
        );

      case 'transactions':
        return (
          <div className="text-white">
            <h3 className="text-xl font-bold mb-6">Transaction History</h3>
            <div className="overflow-x-auto">
              {myTransactions.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-gray-400">No transactions yet</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-700/50">
                    <tr>
                      <th className="text-left py-3 px-4 text-gray-400">Type</th>
                      <th className="text-left py-3 px-4 text-gray-400">Amount</th>
                      <th className="text-left py-3 px-4 text-gray-400">Commission</th>
                      <th className="text-left py-3 px-4 text-gray-400">Net</th>
                      <th className="text-left py-3 px-4 text-gray-400">Status</th>
                      <th className="text-left py-3 px-4 text-gray-400">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myTransactions.map(tx => (
                      <tr key={tx.id} className="border-b border-slate-700 hover:bg-slate-700/50">
                        <td className="py-3 px-4">
                          <span className="inline-block bg-blue-500/20 text-blue-300 px-2 py-1 rounded text-xs">
                            {tx.type === 'wallet_load' ? '💰 Load' : '🏦 Withdraw'}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-semibold">₹{parseFloat(tx.gross_amount).toFixed(2)}</td>
                        <td className="py-3 px-4 text-orange-400">-₹{parseFloat(tx.commission_amount).toFixed(2)}</td>
                        <td className="py-3 px-4 text-emerald-400 font-semibold">₹{parseFloat(tx.net_amount).toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            tx.status === 'success' ? 'bg-green-500/20 text-green-400' :
                            tx.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>{tx.status}</span>
                        </td>
                        <td className="py-3 px-4 text-gray-400 text-xs">{new Date(tx.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );

      case 'withdrawals':
        return (
          <div className="text-white">
            <h3 className="text-xl font-bold mb-6">Withdrawal History</h3>
            <div className="p-6">
              <p className="text-gray-400">No withdrawals yet</p>
            </div>
          </div>
        );

      case 'referrals':
        return (
          <div className="text-white">
            <h3 className="text-xl font-bold mb-4">Your Referral Code</h3>
            <p className="text-gray-400 mb-6">Share this code and earn ₹ from referrals</p>
            <div className="bg-yellow-600 text-slate-900 rounded-lg p-6 inline-flex items-center gap-4">
              <div>
                <p className="text-sm font-semibold">Share this code</p>
                <p className="text-2xl font-bold font-mono">{walletData?.referralCode || 'Loading...'}</p>
              </div>
              <button onClick={handleCopyReferralCode} className="bg-slate-900/30 border-2 border-slate-900 hover:bg-slate-900/50 text-slate-900 font-bold px-4 py-2 rounded transition">
                {copied ? '✓ Copied' : '❐ Copy'}
              </button>
            </div>
          </div>
        );

      case 'manage-users':
        return (
          <div className="text-white">
            <h3 className="text-2xl font-bold mb-4">Manage Users</h3>
            <p className="text-gray-400 mb-6">View and manage user accounts</p>

            {/* Signup Pause Toggle Section */}
            <div className="mb-8 p-6 bg-green-600/20 border border-green-600 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-bold">Pause New Signups</h4>
                  <p className="text-gray-400 text-sm mt-1">
                    {signupPaused ? '🔴 New signups are currently PAUSED' : '🟢 New signups are currently ENABLED'}
                  </p>
                </div>
                <button
                  onClick={handleToggleSignupPause}
                  disabled={toggleLoading}
                  className={`px-6 py-2 font-bold rounded transition ${
                    signupPaused
                      ? 'bg-green-600 hover:bg-green-700 disabled:bg-green-600/50'
                      : 'bg-red-600 hover:bg-red-700 disabled:bg-red-600/50'
                  } text-white`}
                >
                  {toggleLoading ? 'Updating...' : signupPaused ? 'Resume Signups' : 'Pause Signups'}
                </button>
              </div>
            </div>

            {/* Minimum Amount Limits Section */}
            <div className="mb-8 p-6 bg-slate-700/30 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-bold">Minimum Amount Limits</h4>
                <button
                  onClick={() => {
                    setEditingLimits(!editingLimits);
                    if (!editingLimits) setLimitValues(systemLimits);
                  }}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-bold py-1 px-3 rounded transition"
                >
                  {editingLimits ? 'Cancel' : 'Edit'}
                </button>
              </div>
              {editingLimits ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Min Add Money (₹)</label>
                      <input type="number" value={limitValues.min_add_amount} onChange={e => setLimitValues(prev => ({ ...prev, min_add_amount: parseFloat(e.target.value) || 0 }))} className="w-full bg-slate-600 border border-yellow-600 text-white rounded px-3 py-1" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Min Withdraw (₹)</label>
                      <input type="number" value={limitValues.min_withdraw_amount} onChange={e => setLimitValues(prev => ({ ...prev, min_withdraw_amount: parseFloat(e.target.value) || 0 }))} className="w-full bg-slate-600 border border-yellow-600 text-white rounded px-3 py-1" />
                    </div>
                  </div>
                  <button onClick={async () => { try { await adminAPI.updateLimits(limitValues); setSystemLimits(limitValues); setEditingLimits(false); alert('✅ Limits updated'); } catch(e) { alert('Error updating limits'); } }} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition">Save Limits</button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <p><span className="text-gray-400">Min Add Money:</span> <span className="text-yellow-600 font-bold">₹{systemLimits.min_add_amount || 200}</span></p>
                  <p><span className="text-gray-400">Min Withdraw:</span> <span className="text-yellow-600 font-bold">₹{systemLimits.min_withdraw_amount || 100}</span></p>
                </div>
              )}
            </div>

            {/* Default Commissions Section */}
            <div className="mb-8 p-6 bg-slate-700/30 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-bold">Default Commissions</h4>
                <button
                  onClick={() => {
                    setEditingDefaults(!editingDefaults);
                    if (!editingDefaults) setDefaultValues(defaultCommissions);
                  }}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-bold py-1 px-3 rounded transition"
                >
                  {editingDefaults ? 'Cancel' : 'Edit'}
                </button>
              </div>
              
              {editingDefaults ? (
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Referral (%)</label>
                    <input
                      type="number"
                      value={defaultValues.referral}
                      onChange={(e) => handleDefaultCommissionChange('referral', e.target.value)}
                      className="w-full bg-slate-600 border border-slate-500 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-600"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Deposit (%)</label>
                    <input
                      type="number"
                      value={defaultValues.deposit}
                      onChange={(e) => handleDefaultCommissionChange('deposit', e.target.value)}
                      className="w-full bg-slate-600 border border-slate-500 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-600"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Withdraw (%)</label>
                    <input
                      type="number"
                      value={defaultValues.withdraw}
                      onChange={(e) => handleDefaultCommissionChange('withdraw', e.target.value)}
                      className="w-full bg-slate-600 border border-slate-500 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-yellow-600"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-gray-400 text-xs mb-1">Referral</p>
                    <p className="text-lg font-bold text-yellow-600">{defaultCommissions.referral}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-400 text-xs mb-1">Deposit</p>
                    <p className="text-lg font-bold text-yellow-600">{defaultCommissions.deposit}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-400 text-xs mb-1">Withdraw</p>
                    <p className="text-lg font-bold text-yellow-600">{defaultCommissions.withdraw}%</p>
                  </div>
                </div>
              )}

              {editingDefaults && (
                <button
                  onClick={saveDefaultCommissions}
                  className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition"
                >
                  Save Defaults
                </button>
              )}
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-600">
                    <th className="text-left py-3 px-4">
                      <div className="flex flex-col gap-2">
                        <span className="text-sm font-bold">Username</span>
                        <input
                          type="text"
                          placeholder="Search..."
                          value={columnFilters.username}
                          onChange={(e) => handleColumnFilterChange('username', e.target.value)}
                          className="text-xs bg-slate-700 border border-slate-600 text-white rounded px-2 py-1 focus:outline-none focus:border-yellow-600 w-full"
                        />
                      </div>
                    </th>
                    <th className="text-left py-3 px-4">
                      <div className="flex flex-col gap-2">
                        <span className="text-sm font-bold">Email</span>
                        <input
                          type="text"
                          placeholder="Search..."
                          value={columnFilters.email}
                          onChange={(e) => handleColumnFilterChange('email', e.target.value)}
                          className="text-xs bg-slate-700 border border-slate-600 text-white rounded px-2 py-1 focus:outline-none focus:border-yellow-600 w-full"
                        />
                      </div>
                    </th>
                    <th className="text-left py-3 px-4">
                      <div className="flex flex-col gap-2">
                        <span className="text-sm font-bold">A/c Status</span>
                        <select
                          value={columnFilters.accountStatus}
                          onChange={(e) => handleColumnFilterChange('accountStatus', e.target.value)}
                          className="text-xs bg-slate-700 border border-slate-600 text-white rounded px-2 py-1 focus:outline-none focus:border-yellow-600 w-full"
                        >
                          <option value="">All Status</option>
                          <option value="Active">Active</option>
                          <option value="Frozen">Frozen</option>
                          <option value="Terminated">Terminated</option>
                        </select>
                      </div>
                    </th>
                    <th className="text-left py-3 px-4">
                      <div className="flex flex-col gap-2">
                        <span className="text-sm font-bold">Deposit %</span>
                        <input
                          type="text"
                          placeholder="Search..."
                          value={columnFilters.depositCommission}
                          onChange={(e) => handleColumnFilterChange('depositCommission', e.target.value)}
                          className="text-xs bg-slate-700 border border-slate-600 text-white rounded px-2 py-1 focus:outline-none focus:border-yellow-600 w-full"
                        />
                      </div>
                    </th>
                    <th className="text-left py-3 px-4">
                      <div className="flex flex-col gap-2">
                        <span className="text-sm font-bold">Withdraw %</span>
                        <input
                          type="text"
                          placeholder="Search..."
                          value={columnFilters.withdrawCommission}
                          onChange={(e) => handleColumnFilterChange('withdrawCommission', e.target.value)}
                          className="text-xs bg-slate-700 border border-slate-600 text-white rounded px-2 py-1 focus:outline-none focus:border-yellow-600 w-full"
                        />
                      </div>
                    </th>
                    <th className="text-left py-3 px-4">
                      <div className="flex flex-col gap-2">
                        <span className="text-sm font-bold">Referral %</span>
                        <input
                          type="text"
                          placeholder="Search..."
                          value={columnFilters.referralCommission}
                          onChange={(e) => handleColumnFilterChange('referralCommission', e.target.value)}
                          className="text-xs bg-slate-700 border border-slate-600 text-white rounded px-2 py-1 focus:outline-none focus:border-yellow-600 w-full"
                        />
                      </div>
                    </th>
                    <th className="text-left py-3 px-4"><span className="text-sm font-bold">Balance (₹)</span></th>
                    <th className="text-left py-3 px-4"><span className="text-sm font-bold">Transactions</span></th>
                    <th className="text-left py-3 px-4"><span className="text-sm font-bold">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {usersLoading ? (
                    <tr><td colSpan="9" className="text-center py-4 text-gray-400">Loading users...</td></tr>
                  ) : getFilteredUsers().length === 0 ? (
                    <tr><td colSpan="9" className="text-center py-4 text-gray-400">No users found</td></tr>
                  ) : (
                    getFilteredUsers().map(u => (
                      <tr key={u.id} className="border-b border-slate-700 hover:bg-slate-800/50">
                        <td className="py-3 px-4">
                          <button
                            onClick={() => openUserHistory(u)}
                            className="text-yellow-600 hover:underline cursor-pointer"
                          >
                            {u.username}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-300">{u.email}</td>
                        <td className="py-3 px-4 text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              u.account_status === 'active' ? 'bg-green-600 text-white' :
                              u.account_status === 'frozen' ? 'bg-orange-600 text-white' :
                              u.account_status === 'terminated' ? 'bg-red-600 text-white' :
                              'bg-gray-600 text-white'
                            }`}>
                              {u.account_status === 'active' ? '✓ Active' :
                               u.account_status === 'frozen' ? '❄️ Frozen' :
                               u.account_status === 'terminated' ? '⛔ Terminated' :
                               u.account_status}
                            </span>
                            <button
                              onClick={() => {
                                setAccountActionUser(u);
                                setAccountActionMenu(!accountActionMenu && accountActionUser?.id === u.id ? false : true);
                              }}
                              className="text-gray-400 hover:text-yellow-400 text-lg transition"
                              title="Account Management"
                              data-user-action-btn
                            >
                              ⚙️
                            </button>
                            {accountActionMenu && accountActionUser?.id === u.id && (
                              <div id="account-action-dropdown" className="absolute mt-2 bg-slate-700 border border-slate-600 rounded shadow-lg z-50 min-w-48 top-6 right-0">
                                <button
                                  onClick={() => handleUserAction(u.account_status === 'frozen' ? 'unfreeze' : 'freeze')}
                                  disabled={accountActionLoading}
                                  className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-slate-600 disabled:opacity-50"
                                >
                                  {u.account_status === 'frozen' ? '☀️ Unfreeze' : '❄️ Freeze'}
                                </button>
                                <button
                                  onClick={() => handleUserAction(u.account_status === 'terminated' ? 'unterminate' : 'terminate')}
                                  disabled={accountActionLoading}
                                  className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-slate-600 disabled:opacity-50"
                                >
                                  {u.account_status === 'terminated' ? '🔓 Unterminate' : '⛔ Terminate'}
                                </button>
                                <button
                                  onClick={() => handleUserAction(u.withdrawal_paused ? 'resume-withdrawal' : 'pause-withdrawal')}
                                  disabled={accountActionLoading}
                                  className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-slate-600 disabled:opacity-50"
                                >
                                  {u.withdrawal_paused ? '▶️ Resume Withdrawal' : '⏸️ Pause Withdrawal'}
                                </button>
                                <button
                                  onClick={() => handleUserAction('delete')}
                                  disabled={accountActionLoading}
                                  className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-slate-600 disabled:opacity-50"
                                >
                                  🗑️ Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {editingUserId === u.id && editingField === 'depositCommission' ? (
                            <input
                              type="number"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="w-16 bg-slate-600 border border-yellow-600 text-white rounded px-2 py-1 text-xs"
                              min="0"
                              max="100"
                              autoFocus
                            />
                          ) : (
                            <span
                              onClick={() => startEditing(u.id, 'depositCommission', u.depositCommission || defaultCommissions.deposit)}
                              className="cursor-pointer hover:text-yellow-600"
                            >
                              {u.depositCommission !== undefined ? u.depositCommission : defaultCommissions.deposit}%
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {editingUserId === u.id && editingField === 'withdrawCommission' ? (
                            <input
                              type="number"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="w-16 bg-slate-600 border border-yellow-600 text-white rounded px-2 py-1 text-xs"
                              min="0"
                              max="100"
                              autoFocus
                            />
                          ) : (
                            <span
                              onClick={() => startEditing(u.id, 'withdrawCommission', u.withdrawCommission || defaultCommissions.withdraw)}
                              className="cursor-pointer hover:text-yellow-600"
                            >
                              {u.withdrawCommission !== undefined ? u.withdrawCommission : defaultCommissions.withdraw}%
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {editingUserId === u.id && editingField === 'referralCommission' ? (
                            <input
                              type="number"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="w-16 bg-slate-600 border border-yellow-600 text-white rounded px-2 py-1 text-xs"
                              autoFocus
                            />
                          ) : (
                            <span
                              onClick={() => startEditing(u.id, 'referralCommission', u.referralCommission || 0)}
                              className="cursor-pointer hover:text-yellow-600"
                            >
                              {u.referralCommission || 0}%
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm font-bold text-yellow-600">
                          {editingUserId === u.id && editingField === 'balance' ? (
                            <input
                              type="number"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="w-24 bg-slate-600 border border-yellow-600 text-white rounded px-2 py-1 text-xs"
                              autoFocus
                            />
                          ) : (
                            <span
                              onClick={() => startEditing(u.id, 'balance', u.wallet_balance || 0)}
                              className="cursor-pointer hover:text-white"
                            >
                              ₹{parseFloat(u.wallet_balance || 0).toFixed(2)}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-300">{u.transactionCount || 0}</td>
                        <td className="py-3 px-4 text-sm">
                          {editingUserId === u.id ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => saveEdit(u.id)}
                                className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold py-1 px-2 rounded"
                              >
                                Save
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="bg-gray-600 hover:bg-gray-700 text-white text-xs font-bold py-1 px-2 rounded"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => openUserHistory(u)}
                              className="text-blue-400 hover:text-blue-300 text-lg"
                              title="View History"
                            >
                              👁️
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* User History Modal */}
            {selectedUserHistory && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-slate-800 rounded-lg p-6 w-4/5 max-h-96 overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-xl font-bold text-white">History - {selectedUserHistory.username}</h4>
                    <button
                      onClick={closeUserHistory}
                      className="text-gray-400 hover:text-white text-2xl"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Transaction Search */}
                  <input
                    type="text"
                    placeholder="Search transactions (Order No., Amount, Type, Status)..."
                    value={transactionSearch}
                    onChange={(e) => setTransactionSearch(e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 mb-4 focus:outline-none focus:border-yellow-600"
                  />

                  {/* Transaction Table */}
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-600">
                        <th className="text-left py-2 px-3 text-gray-400">Order No.</th>
                        <th className="text-left py-2 px-3 text-gray-400">Amount</th>
                        <th className="text-left py-2 px-3 text-gray-400">Type</th>
                        <th className="text-left py-2 px-3 text-gray-400">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredTransactions().map((t, idx) => (
                        <tr key={idx} className="border-b border-slate-700 hover:bg-slate-700/50">
                          <td className="py-2 px-3 text-white">{t.orderNo}</td>
                          <td className="py-2 px-3 text-yellow-600 font-bold">₹{t.amount}</td>
                          <td className="py-2 px-3 text-gray-300 text-xs">{t.type}</td>
                          <td className="py-2 px-3">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              t.status === 'success' ? 'bg-green-500/30 text-green-400' : 'bg-yellow-500/30 text-yellow-400'
                            }`}>
                              {t.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      case 'api-config':
        return (
          <div className="text-white">
            <h3 className="text-2xl font-bold mb-4">API Configuration</h3>
            <p className="text-gray-400 mb-6">Manage BondPay API credentials</p>
            
            {apiConfigSuccess && (
              <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded mb-4">{apiConfigSuccess}</div>
            )}

            <form onSubmit={handleSaveApiConfig} className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Merchant ID</label>
                <input
                  type="text"
                  name="merchant_id"
                  value={apiConfig.merchant_id}
                  onChange={handleApiConfigChange}
                  placeholder="Enter Merchant ID"
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 focus:outline-none focus:border-yellow-600"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Payin API Key</label>
                <div className="flex gap-2">
                  <input
                    type={showPasswords.payin ? 'text' : 'password'}
                    name="payin_api_key"
                    value={apiConfig.payin_api_key}
                    onChange={handleApiConfigChange}
                    placeholder="Enter Payin API Key"
                    className="flex-1 bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 focus:outline-none focus:border-yellow-600"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, payin: !prev.payin }))}
                    className="bg-slate-700 border border-slate-600 text-gray-400 hover:text-white px-3 rounded transition"
                  >
                    {showPasswords.payin ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Payout API Key</label>
                <div className="flex gap-2">
                  <input
                    type={showPasswords.payout ? 'text' : 'password'}
                    name="payout_api_key"
                    value={apiConfig.payout_api_key}
                    onChange={handleApiConfigChange}
                    placeholder="Enter Payout API Key"
                    className="flex-1 bg-slate-700 border border-slate-600 text-white rounded px-4 py-2 focus:outline-none focus:border-yellow-600"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, payout: !prev.payout }))}
                    className="bg-slate-700 border border-slate-600 text-gray-400 hover:text-white px-3 rounded transition"
                  >
                    {showPasswords.payout ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={apiConfigSaving}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded transition disabled:opacity-50"
              >
                {apiConfigSaving ? 'Saving...' : 'Save Configuration'}
              </button>
            </form>

            {/* Database Export/Import Section */}
            <div className="mt-8 border-t border-slate-700 pt-6">
              <h4 className="text-xl font-bold mb-4 text-yellow-600">Database Management</h4>
              <div className="space-y-4 max-w-md">
                <button
                  onClick={exportDatabase}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition flex items-center justify-center gap-2"
                >
                  📥 Download Database
                </button>
                
                <div className="relative">
                  <input
                    type="file"
                    id="dbImport"
                    accept=".json"
                    onChange={importDatabase}
                    className="hidden"
                  />
                  <button
                    onClick={() => document.getElementById('dbImport').click()}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition flex items-center justify-center gap-2"
                  >
                    📤 Upload Database
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">⚠️ Uploading will overwrite all user data. Backup before importing.</p>
              </div>
            </div>
          </div>
        );


      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-screen bg-slate-900">
      {/* Header */}
      <nav className="bg-slate-800 border-b border-slate-700 shadow-lg sticky top-0 z-50">
        <div className="px-4 md:px-6 py-2 flex items-center justify-between md:justify-start md:gap-12">
          <img src="/logo.svg" alt="PaySolutions" className="h-16 md:h-20 w-auto flex-shrink-0 object-contain" />
          
          {/* Desktop: Wallet and Referral Info - Center */}
          <div className="hidden md:flex items-center gap-8 flex-1">
            {/* Wallet Balance */}
            <div className="flex items-center gap-3">
              <svg width="28" height="28" viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg">
                <defs><linearGradient id="walletGold" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD84D"/><stop offset="100%" stopColor="#F4B400"/></linearGradient></defs>
                <rect x="20" y="40" width="88" height="60" fill="url(#walletGold)" rx="8"/><rect x="30" y="32" width="68" height="12" fill="url(#walletGold)" rx="4"/><circle cx="88" cy="70" r="8" fill="white"/>
              </svg>
              <div>
                <p className="text-xs text-gray-400">Wallet</p>
                <p className="text-sm font-bold text-yellow-600">₹{walletData?.walletBalance ? parseFloat(walletData.walletBalance).toFixed(2) : '0.00'}</p>
              </div>
            </div>
            
            {/* Referral Balance */}
            <div className="flex items-center gap-3">
              <svg width="28" height="28" viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg">
                <defs><linearGradient id="refGold" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD84D"/><stop offset="100%" stopColor="#F4B400"/></linearGradient></defs>
                <circle cx="48" cy="40" r="16" fill="url(#refGold)"/><rect x="28" y="62" width="40" height="24" fill="url(#refGold)" rx="4"/><circle cx="88" cy="40" r="16" fill="url(#refGold)"/><rect x="68" y="62" width="40" height="24" fill="url(#refGold)" rx="4"/><path d="M 48 86 Q 48 98 58 104 Q 68 110 76 110 Q 84 110 94 104 Q 104 98 104 86" fill="url(#refGold)"/>
              </svg>
              <div>
                <p className="text-xs text-gray-400">Referral</p>
                <p className="text-sm font-bold text-yellow-600">₹{walletData?.referralEarnings?.total_earned ? parseFloat(walletData.referralEarnings.total_earned).toFixed(2) : '0.00'}</p>
              </div>
            </div>
          </div>
          
          {/* Mobile: Wallet and Referral Info - Right side, compact */}
          <div className="flex md:hidden items-center gap-2 mr-2">
            {/* Wallet Icon + Amount */}
            <div className="flex items-center gap-1">
              <svg width="18" height="18" viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg">
                <defs><linearGradient id="walletGoldMobHeader" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD84D"/><stop offset="100%" stopColor="#F4B400"/></linearGradient></defs>
                <rect x="20" y="40" width="88" height="60" fill="url(#walletGoldMobHeader)" rx="8"/><rect x="30" y="32" width="68" height="12" fill="url(#walletGoldMobHeader)" rx="4"/><circle cx="88" cy="70" r="8" fill="white"/>
              </svg>
              <p className="text-xs font-bold text-yellow-600">₹{walletData?.walletBalance ? parseFloat(walletData.walletBalance).toFixed(0) : '0'}</p>
            </div>
            
            {/* Referral Icon + Amount */}
            <div className="flex items-center gap-1">
              <svg width="18" height="18" viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg">
                <defs><linearGradient id="refGoldMobHeader" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD84D"/><stop offset="100%" stopColor="#F4B400"/></linearGradient></defs>
                <circle cx="48" cy="40" r="16" fill="url(#refGoldMobHeader)"/><rect x="28" y="62" width="40" height="24" fill="url(#refGoldMobHeader)" rx="4"/><circle cx="88" cy="40" r="16" fill="url(#refGoldMobHeader)"/><rect x="68" y="62" width="40" height="24" fill="url(#refGoldMobHeader)" rx="4"/><path d="M 48 86 Q 48 98 58 104 Q 68 110 76 110 Q 84 110 94 104 Q 104 98 104 86" fill="url(#refGoldMobHeader)"/>
              </svg>
              <p className="text-xs font-bold text-yellow-600">₹{walletData?.referralEarnings?.total_earned ? parseFloat(walletData.referralEarnings.total_earned).toFixed(0) : '0'}</p>
            </div>
          </div>
          
          <button onClick={logout} className="hover:opacity-80 transition-all duration-200 ml-auto" title="Logout">
            <svg width="32" height="32" viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <mask id="logoutMask">
                  <rect width="128" height="128" fill="white"/>
                  <path
                    d="M44 64
                       H82
                       M70 50
                       L88 64
                       L70 78"
                    fill="none"
                    stroke="black"
                    stroke-width="11"
                    stroke-linecap="round"
                    stroke-linejoin="round"/>
                </mask>
                <linearGradient id="gold" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#FFD84D"/>
                  <stop offset="100%" stop-color="#F4B400"/>
                </linearGradient>
              </defs>
              <rect
                  x="22"
                  y="12"
                  width="72"
                  height="104"
                  rx="18"
                  fill="url(#gold)"
                  mask="url(#logoutMask)"/>
            </svg>
          </button>
        </div>
      </nav>

      {/* Main Layout */}
      <div className="flex h-[calc(100vh-96px)]">
        {/* Left Sidebar - Menu Icons */}
        <div className="flex flex-col items-start justify-start gap-4 md:gap-6 py-4 md:py-6 px-2 md:px-4 ml-1 md:ml-4">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveMenu(item.id)}
              className="sidebar-menu-btn group relative"
              title={item.label}
            >
              <div className="flex items-center gap-3">
                {/* Icon - Yellow SVG only */}
                <div className="transition-all duration-200 w-6 md:w-8 h-6 md:h-8">
                  {item.id === 'overview' && (
                    <svg viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                      <defs><linearGradient id="gold1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD84D"/><stop offset="100%" stopColor="#F4B400"/></linearGradient></defs>
                      <rect x="20" y="60" width="20" height="48" fill="url(#gold1)" rx="4"/><rect x="54" y="40" width="20" height="68" fill="url(#gold1)" rx="4"/><rect x="88" y="20" width="20" height="88" fill="url(#gold1)" rx="4"/>
                    </svg>
                  )}
                  {item.id === 'profile' && (
                    <svg viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                      <defs><linearGradient id="goldProfile" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD84D"/><stop offset="100%" stopColor="#F4B400"/></linearGradient></defs>
                      <circle cx="64" cy="40" r="20" fill="url(#goldProfile)"/><path d="M 30 90 Q 30 70 64 70 Q 98 70 98 90 L 98 108 Q 98 110 96 110 L 32 110 Q 30 110 30 108 Z" fill="url(#goldProfile)"/>
                    </svg>
                  )}
                  {item.id === 'add-money' && (
                    <svg viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                      <defs><linearGradient id="gold2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD84D"/><stop offset="100%" stopColor="#F4B400"/></linearGradient></defs>
                      <rect x="20" y="30" width="88" height="68" fill="url(#gold2)" rx="8"/><circle cx="64" cy="64" r="20" fill="none" stroke="white" strokeWidth="6"/><line x1="64" y1="50" x2="64" y2="78" stroke="white" strokeWidth="6" strokeLinecap="round"/><line x1="50" y1="64" x2="78" y2="64" stroke="white" strokeWidth="6" strokeLinecap="round"/>
                    </svg>
                  )}
                  {item.id === 'withdraw' && (
                    <svg viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                      <defs><linearGradient id="gold3" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD84D"/><stop offset="100%" stopColor="#F4B400"/></linearGradient></defs>
                      <rect x="22" y="35" width="84" height="70" fill="url(#gold3)" rx="8"/><rect x="35" y="50" width="28" height="35" fill="none" stroke="white" strokeWidth="5" rx="2"/><rect x="65" y="50" width="28" height="35" fill="none" stroke="white" strokeWidth="5" rx="2"/><rect x="22" y="95" width="84" height="8" fill="white"/>
                    </svg>
                  )}
                  {item.id === 'transactions' && (
                    <svg viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                      <defs><linearGradient id="gold4" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD84D"/><stop offset="100%" stopColor="#F4B400"/></linearGradient></defs>
                      <rect x="25" y="20" width="78" height="88" fill="url(#gold4)" rx="8"/><line x1="35" y1="35" x2="93" y2="35" stroke="white" strokeWidth="4" strokeLinecap="round"/><line x1="35" y1="52" x2="93" y2="52" stroke="white" strokeWidth="4" strokeLinecap="round"/><line x1="35" y1="69" x2="93" y2="69" stroke="white" strokeWidth="4" strokeLinecap="round"/><line x1="35" y1="86" x2="75" y2="86" stroke="white" strokeWidth="4" strokeLinecap="round"/>
                    </svg>
                  )}
                  {item.id === 'withdrawals' && (
                    <svg viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                      <defs><linearGradient id="gold5" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD84D"/><stop offset="100%" stopColor="#F4B400"/></linearGradient></defs>
                      <rect x="25" y="30" width="78" height="68" fill="url(#gold5)" rx="8"/><path d="M 64 70 L 64 40 M 50 54 L 64 40 L 78 54" stroke="white" strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round"/><line x1="30" y1="88" x2="98" y2="88" stroke="white" strokeWidth="4" strokeLinecap="round"/>
                    </svg>
                  )}
                  {item.id === 'referrals' && (
                    <svg viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                      <defs><linearGradient id="gold6" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD84D"/><stop offset="100%" stopColor="#F4B400"/></linearGradient></defs>
                      <circle cx="64" cy="50" r="28" fill="url(#gold6)"/><circle cx="64" cy="50" r="12" fill="white"/><circle cx="42" cy="88" r="18" fill="url(#gold6)"/><circle cx="42" cy="88" r="8" fill="white"/><circle cx="86" cy="88" r="18" fill="url(#gold6)"/><circle cx="86" cy="88" r="8" fill="white"/>
                    </svg>
                  )}
                  {item.id === 'manage-users' && (
                    <svg viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                      <defs><linearGradient id="gold7" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD84D"/><stop offset="100%" stopColor="#F4B400"/></linearGradient></defs>
                      <circle cx="38" cy="35" r="15" fill="url(#gold7)"/><rect x="20" y="58" width="36" height="30" fill="url(#gold7)" rx="4"/><circle cx="90" cy="35" r="15" fill="url(#gold7)"/><rect x="72" y="58" width="36" height="30" fill="url(#gold7)" rx="4"/><path d="M 35 88 Q 35 100 45 105 Q 55 110 64 110 Q 73 110 83 105 Q 93 100 93 88" fill="url(#gold7)"/>
                    </svg>
                  )}
                  {item.id === 'api-config' && (
                    <svg viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                      <defs><linearGradient id="gold8" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD84D"/><stop offset="100%" stopColor="#F4B400"/></linearGradient></defs>
                      <circle cx="64" cy="64" r="22" fill="url(#gold8)"/><circle cx="64" cy="64" r="12" fill="white"/><circle cx="64" cy="38" r="5" fill="url(#gold8)"/><circle cx="82" cy="52" r="5" fill="url(#gold8)"/><circle cx="82" cy="76" r="5" fill="url(#gold8)"/><circle cx="64" cy="90" r="5" fill="url(#gold8)"/><circle cx="46" cy="76" r="5" fill="url(#gold8)"/><circle cx="46" cy="52" r="5" fill="url(#gold8)"/>
                    </svg>
                  )}
                </div>
                
                {/* Label - Transparent by default, visible on hover */}
                <span
                  className={`text-xs md:text-sm font-bold transition-all duration-200 overflow-hidden ${
                    activeMenu === item.id
                      ? 'text-yellow-600'
                      : 'text-slate-300 opacity-0 group-hover:opacity-100 group-hover:text-yellow-600'
                  }`}
                >
                  {item.label}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto p-3 md:p-8 md:pr-4 md:pl-6 flex items-start justify-center">
          <div className="w-full max-w-7xl">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
